
import React from 'react'
import clsx from 'clsx'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
    resolveTableViewId,
    useTableViewDropdownContext,
} from 'components/Ui/TableViewDropdownContext'
import UiTableColumnVisibilityPanel from 'components/Ui/UiTableColumnVisibilityPanel/UiTableColumnVisibilityPanel'
import UiTableViewDropdownPanel, { toTableSaveViewApi } from 'components/Ui/UiTableViewDropdownPanel'

interface UiTableColumnVisibilityToggleProps<TData> {
    table: Table<TData>
    triggerClassName?: string
}

export const UiTableColumnVisibilityToggle = <TData,>({
    table,
    triggerClassName,
}: UiTableColumnVisibilityToggleProps<TData>) => {
    const { t } = useTranslation()
    const [open, setOpen] = React.useState(false)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const tableViewContext = useTableViewDropdownContext()
    const tableViewId = resolveTableViewId((table.options.meta as { tableId?: string } | undefined)?.tableId)

    React.useEffect(() => {
        if (!open) {
            return
        }

        const handler = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const canShowFullViewPanel = Boolean(tableViewContext && tableViewId)

    return (
        <div
            ref={containerRef}
            className="UiTableColumnVisibilityToggle relative inline-block text-left h-full flex items-center justify-end w-full"
        >
            <button
                type="button"
                aria-label={t('View') as string}
                title={t('View') as string}
                className={clsx('flex items-center justify-center focus:outline-none', triggerClassName)}
                onClick={() => setOpen((previous) => !previous)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                    data-slot="icon"
                    className="w-4 h-4 mr-1.5 flex-none text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                    <path
                        fillRule="evenodd"
                        d="M12 6.75a5.25 5.25 0 0 1 6.775-5.025.75.75 0 0 1 .313 1.248l-3.32 3.319c.063.475.276.934.641 1.299.365.365.824.578 1.3.64l3.318-3.319a.75.75 0 0 1 1.248.313 5.25 5.25 0 0 1-5.472 6.756c-1.018-.086-1.87.1-2.309.634L7.344 21.3A3.298 3.298 0 1 1 2.7 16.657l8.684-7.151c.533-.44.72-1.291.634-2.309A5.342 5.342 0 0 1 12 6.75ZM4.117 19.125a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75h-.008a.75.75 0 0 1-.75-.75v-.008Z"
                        clipRule="evenodd"
                    ></path>
                    <path d="m10.076 8.64-2.201-2.2V4.874a.75.75 0 0 0-.364-.643l-3.75-2.25a.75.75 0 0 0-.916.113l-.75.75a.75.75 0 0 0-.113.916l2.25 3.75a.75.75 0 0 0 .643.364h1.564l2.062 2.062 1.575-1.297Z"></path>
                    <path
                        fillRule="evenodd"
                        d="m12.556 17.329 4.183 4.182a3.375 3.375 0 0 0 4.773-4.773l-3.306-3.305a6.803 6.803 0 0 1-1.53.043c-.394-.034-.682-.006-.867.042a.589.589 0 0 0-.167.063l-3.086 3.748Zm3.414-1.36a.75.75 0 0 1 1.06 0l1.875 1.876a.75.75 0 1 1-1.06 1.06L15.97 17.03a.75.75 0 0 1 0-1.06Z"
                        clipRule="evenodd"
                    ></path>
                </svg>
            </button>

            {canShowFullViewPanel && tableViewContext && tableViewId ? (
                <UiTableViewDropdownPanel
                    tableInit={toTableSaveViewApi(tableViewContext.tableInit)}
                    tableId={tableViewId}
                    open={open}
                    onClose={() => setOpen(false)}
                />
            ) : (
                open && (
                    <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                            {t('Columns')}
                        </div>
                        <UiTableColumnVisibilityPanel table={table} enabled={open} onApplied={() => setOpen(false)} />
                    </div>
                )
            )}
        </div>
    )
}

export default UiTableColumnVisibilityToggle
